/**
 * useWritingSession.ts
 *
 * Psychology-powered session management for Write Library.
 *
 * Implements:
 *   1. Breadcrumb Trail — writer leaves a note for their future self at session end.
 *      It appears as ghost text next time they open the same chapter.
 *
 *   2. Session-End Micro-Moment — specific, never generic acknowledgment of
 *      what the writer actually accomplished.
 *
 *   3. Friction Journal — gentle check-in when writer stops abruptly.
 *      Tracks patterns, surfaces tailored nudges.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '../lib/store';

const STORAGE_PREFIX = 'wl_breadcrumb_';
const FRICTION_STORAGE_KEY = 'wl_friction_journal';
const INACTIVITY_THRESHOLD_MS = 3 * 60 * 1000; // 3 mins

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SessionSummary {
    wordsAdded: number;
    duration: number; // seconds
    chaptersWorkedOn: string[];
    compostSaved: number;
    streakDay: number;
    message: string;
    breadcrumbPrompt: string;
}

export interface BreadcrumbNote {
    chapterId: string;
    text: string;
    createdAt: string;
}

export type FrictionReason =
    | 'social_media'
    | 'self_doubt'
    | 'plot_problem'
    | 'life_stuff'
    | 'flow_state_ended'
    | 'skip';

interface FrictionEntry {
    date: string;
    reason: FrictionReason;
}

// ─── Breadcrumb Storage ───────────────────────────────────────────────────────

function saveBreadcrumb(chapterId: string, text: string) {
    const note: BreadcrumbNote = {
        chapterId,
        text,
        createdAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_PREFIX + chapterId, JSON.stringify(note));
}

export function getBreadcrumb(chapterId: string): BreadcrumbNote | null {
    try {
        const raw = localStorage.getItem(STORAGE_PREFIX + chapterId);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export function clearBreadcrumb(chapterId: string) {
    localStorage.removeItem(STORAGE_PREFIX + chapterId);
}

// ─── Friction Journal Storage ─────────────────────────────────────────────────

function getFrictionHistory(): FrictionEntry[] {
    try {
        const raw = localStorage.getItem(FRICTION_STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function saveFrictionEntry(reason: FrictionReason) {
    const history = getFrictionHistory();
    history.push({ date: new Date().toISOString(), reason });
    localStorage.setItem(FRICTION_STORAGE_KEY, JSON.stringify(history.slice(-30)));
}

function getFrictionNudge(reason: FrictionReason): string | null {
    const history = getFrictionHistory();
    const recentSame = history.filter(e => e.reason === reason).slice(-5);
    if (recentSame.length < 3) return null;

    const nudges: Record<FrictionReason, string | null> = {
        social_media: "You've mentioned social media pulling you away a few times. Consider blocking it for your next session — even 20 minutes uninterrupted changes everything.",
        self_doubt: "Self-doubt has come up a few times. That's actually a sign you care deeply about your work. First drafts are supposed to be messy — that's the whole point.",
        plot_problem: "Looks like plot tangles keep interrupting you. Try the 'What does each character WANT in this scene?' question before you start writing.",
        life_stuff: "Life keeps getting in the way — that's okay. Even 15 minutes counts. Your story will wait for you.",
        flow_state_ended: "Your flow sessions seem to end naturally after a while. That's healthy. Consider scheduling shorter, focused blocks.",
        skip: null,
    };

    return nudges[reason] ?? null;
}

// ─── Session Summary Message Generator ───────────────────────────────────────

function generateSessionMessage(summary: Omit<SessionSummary, 'message' | 'breadcrumbPrompt'>): string {
    const { wordsAdded, duration, streakDay, compostSaved } = summary;
    const minutes = Math.round(duration / 60);

    if (wordsAdded === 0 && minutes < 2) return "You showed up. That's the hardest part.";
    if (compostSaved > 200) return `You made ${compostSaved} words safer — the Compost Heap holds them now. Nothing lost.`;
    if (wordsAdded > 1500) return `${wordsAdded.toLocaleString()} words. That's a short story's worth in one sitting.`;
    if (wordsAdded > 500) return `${wordsAdded.toLocaleString()} words closer to "The End."`;
    if (wordsAdded > 0 && minutes > 45) return `${minutes} minutes of deep work. ${wordsAdded} words that didn't exist before.`;
    if (wordsAdded > 0) return `${wordsAdded} words added. Every sentence counts.`;
    if (streakDay >= 7) return `Day ${streakDay} in a row. The story is being built, one session at a time.`;
    if (minutes > 30) return `${minutes} minutes with your story. Planning and thinking count too.`;
    return 'Session complete. See you next time.';
}

function generateBreadcrumbPrompt(_chaptersWorkedOn: string[]): string {
    const suggestions = [
        "What does your character want most in the next scene?",
        "What's the one thing that needs to happen next?",
        "What's the tension that needs to build?",
        "What question does the reader have right now?",
        "What would surprise even you in the next scene?",
    ];
    return suggestions[Math.floor(Math.random() * suggestions.length)];
}

// ─── Main Hook ────────────────────────────────────────────────────────────────

export function useWritingSession() {
    const novels = useAppStore(s => s.novels);
    const activeNovelId = useAppStore(s => s.activeNovelId);
    const streak = useAppStore(s => s.streak);
    const startWritingSession = useAppStore(s => s.startWritingSession);
    const endWritingSession = useAppStore(s => s.endWritingSession);
    const recordDailyWords = useAppStore(s => s.recordDailyWords);

    const [sessionSummary, setSessionSummary] = useState<SessionSummary | null>(null);
    const [showFrictionCheck, setShowFrictionCheck] = useState(false);
    const [frictionNudge, setFrictionNudge] = useState<string | null>(null);
    const [breadcrumbForChapter, setBreadcrumbForChapter] = useState<string | null>(null);

    const sessionStartWords = useRef<number>(0);
    const sessionStartCompost = useRef<number>(0);
    const sessionStartTime = useRef<Date | null>(null);
    const chaptersEdited = useRef<Set<string>>(new Set());
    const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isSessionActive = useRef(false);

    const activeNovel = novels.find(n => n.id === activeNovelId);

    // ── Begin session ──────────────────────────────────────────────────────────

    const beginSession = useCallback(() => {
        if (isSessionActive.current) return;
        isSessionActive.current = true;

        startWritingSession();
        sessionStartTime.current = new Date();
        sessionStartWords.current = activeNovel?.chapters.reduce((s, ch) => s + ch.wordCount, 0) ?? 0;
        sessionStartCompost.current = activeNovel?.compost.reduce((s, c) => s + c.wordCount, 0) ?? 0;
        chaptersEdited.current.clear();
    }, [startWritingSession, activeNovel]);

    // ── Record chapter edit ────────────────────────────────────────────────────

    const recordChapterEdit = useCallback((chapterId: string) => {
        chaptersEdited.current.add(chapterId);

        if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        inactivityTimer.current = setTimeout(() => {
            if (isSessionActive.current) {
                setShowFrictionCheck(true);
            }
        }, INACTIVITY_THRESHOLD_MS);
    }, []);

    // ── End session and compute summary ────────────────────────────────────────

    const endSession = useCallback((chapterId?: string) => {
        if (!isSessionActive.current) return;
        isSessionActive.current = false;

        if (inactivityTimer.current) {
            clearTimeout(inactivityTimer.current);
            inactivityTimer.current = null;
        }

        endWritingSession();

        const now = new Date();
        const startTime = sessionStartTime.current ?? now;
        const duration = Math.floor((now.getTime() - startTime.getTime()) / 1000);

        const currentWords = activeNovel?.chapters.reduce((s, ch) => s + ch.wordCount, 0) ?? 0;
        const currentCompost = activeNovel?.compost.reduce((s, c) => s + c.wordCount, 0) ?? 0;
        const wordsAdded = Math.max(0, currentWords - sessionStartWords.current);
        const compostSaved = Math.max(0, currentCompost - sessionStartCompost.current);

        if (wordsAdded > 0) {
            recordDailyWords(wordsAdded);
        }

        const chaptersWorkedOn = Array.from(chaptersEdited.current);

        const summaryBase = {
            wordsAdded,
            duration,
            chaptersWorkedOn,
            compostSaved,
            streakDay: streak.currentStreak,
        };

        const summary: SessionSummary = {
            ...summaryBase,
            message: generateSessionMessage(summaryBase),
            breadcrumbPrompt: generateBreadcrumbPrompt(chaptersWorkedOn),
        };

        if (duration > 60 || wordsAdded > 10) {
            setSessionSummary(summary);
            if (chapterId) setBreadcrumbForChapter(chapterId);
            else if (chaptersWorkedOn.length > 0) setBreadcrumbForChapter(chaptersWorkedOn[chaptersWorkedOn.length - 1]);
        }

        setShowFrictionCheck(false);
    }, [endWritingSession, activeNovel, recordDailyWords, streak]);

    // ── Save breadcrumb ────────────────────────────────────────────────────────

    const saveBreadcrumbNote = useCallback((text: string, chapterId?: string) => {
        const id = chapterId || breadcrumbForChapter;
        if (!id || !text.trim()) return;
        saveBreadcrumb(id, text);
    }, [breadcrumbForChapter]);

    // ── Dismiss summary ────────────────────────────────────────────────────────

    const dismissSummary = useCallback(() => {
        setSessionSummary(null);
        setBreadcrumbForChapter(null);
    }, []);

    // ── Handle friction journal ────────────────────────────────────────────────

    const recordFriction = useCallback((reason: FrictionReason) => {
        if (reason !== 'skip') saveFrictionEntry(reason);
        const nudge = getFrictionNudge(reason);
        setFrictionNudge(nudge);
        setShowFrictionCheck(false);
    }, []);

    const dismissFrictionNudge = useCallback(() => {
        setFrictionNudge(null);
    }, []);

    // ── Cleanup on unmount ─────────────────────────────────────────────────────

    useEffect(() => {
        return () => {
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        };
    }, []);

    return {
        // State
        sessionSummary,
        showFrictionCheck,
        frictionNudge,
        breadcrumbForChapter,
        isSessionActive: isSessionActive.current,

        // Actions
        beginSession,
        recordChapterEdit,
        endSession,
        saveBreadcrumbNote,
        dismissSummary,
        recordFriction,
        dismissFrictionNudge,

        // Helpers for the UI
        getBreadcrumbForChapter: getBreadcrumb,
        clearBreadcrumbForChapter: clearBreadcrumb,
    };
}
